import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../layouts/Modal';
import Button from '../misc/Button';
import Avatar from '../misc/Avatar';
import { getUserId } from '../../services/httpService';
import introductionService from '../../services/introductionService';

interface IntroductionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  userId: number;
  alias: string;
}

export default function IntroductionModal({ open, setOpen, userId, alias }: IntroductionModalProps): JSX.Element {
  const { t } = useTranslation();
  const [personalConnections, setPersonalConnections] = useState<any[]>([]);
  const [compatible, setCompatible] = useState<number[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setLoading(true);
    import('../../services/connectionService')
      .then((m) => m.getMyConnections())
      .then((conns) => {
        setPersonalConnections(conns || []);
        const otherIds = (conns || []).map((c: any) => c.id).filter((id: number) => id && id !== userId);
        return introductionService.getCompatibleUserids(userId, otherIds as number[]);
      })
      .then((comp) => {
        setCompatible(comp || []);
      })
      .catch((err) => {
        console.error('Failed to load compatible connections', err);
        setCompatible([]);
        setPersonalConnections([]);
      })
      .finally(() => setLoading(false));
  }, [open, userId]);

  const handleToggle = (id: number) => {
    if (selected.includes(id)) setSelected(selected.filter((i) => i !== id));
    else setSelected([...selected, id]);
  };

  const handleSubmit = async () => {
    try {
      await introductionService.createIntroductions(userId, selected);
      toast.success('Introductions sent!');
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.error.general'));
    }
  };

  const myUserId = getUserId();

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={t('page.home.connections.introduceTo')}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
            {t('general.cancel')}
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={!selected.length}>
            {t('general.save')}
          </Button>
        </div>
      }
      maxWidth="max-w-lg">
      <div className="min-h-[200px]">
        <p className="mb-4 text-sm">
          {t('page.home.connections.introduceToDescription', { name: alias?.replace('+', ' ') })}
        </p>

        <div className="flex max-h-60 flex-col gap-2 overflow-auto">
          {loading && <p className="text-primary-600 text-sm">{t('general.loading')}</p>}
          {!loading &&
            (() => {
              const filtered = personalConnections.filter((c) => c.id && userId !== c.id && c.id !== +myUserId);
              const anyAvailable = filtered.some((c) => c.id && compatible?.includes(c.id));

              if (!filtered.length) {
                return <p className="text-primary-600 text-sm">{t('page.home.connections.introduceEmpty')}</p>;
              }

              return (
                <>
                  {!anyAvailable && (
                    <p className="text-primary-600 text-sm">
                      {t('page.home.connections.introduceAllDisabled', { name: alias?.replace('+', ' ') })}
                    </p>
                  )}

                  {filtered.map((c: any) => {
                    const { id } = c;
                    const disabled = !compatible?.includes(id);
                    const name = `${c.firstName} ${c.lastName}`;
                    return (
                      <label
                        key={id}
                        className={`hover:bg-secondary-100 flex items-center gap-3 rounded p-2 ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={selected.includes(id)}
                          onChange={() => !disabled && handleToggle(id)}
                          disabled={disabled}
                        />
                        <Avatar src={c.picture} alias={name} className="h-8 w-8 text-sm" />
                        <div className="flex-1">
                          <div className="font-medium">{name}</div>
                        </div>
                      </label>
                    );
                  })}
                </>
              );
            })()}
        </div>
      </div>
    </Modal>
  );
}
